import React from 'react';
import { withSentryReactRouterV6Routing } from '@sentry/react';
import { compose } from 'redux';
import { Routes, Route, Navigate } from 'react-router-dom';
import UniDashboardPage from './components/pages/uni-dashboard';
import { P, hasPerm } from './utilities/roles/permissions';
import { useSelector } from 'react-redux';
import { ApplicationState } from './store';
import { selectIntakeAfterCutoff } from './store/selectors';
import Dashboard from './components/pages/dashboard/container';
// applicant-dashboard + every `/application/*` route was extracted to
// `apply/frontend/apply-uni/` — see drifting-snacking-sprout.md.

import WatchInterview, { Questions } from './components/pages/watchInterview';
import wrapPage from './components/templates/page-wrap/hoc';
import { withContainer } from './components/templates/page-wrap/withContainer';
import LegalAdviseDisclaimer from './components/pages/landing/container';
import InfoProcessing from './components/pages/infoProcessing/container';
import PersonalInfoSectionStart from './components/pages/personal-info/sectionStart/container';
import PersonalInfoSectionIntro from './components/pages/personal-info/intro/container';
import PersonalInfoQPage1 from './components/pages/personal-info/qPage1';
import PersonalInfoQPage2 from './components/pages/personal-info/qPage2';
import PersonalInfoQPage3 from './components/pages/personal-info/qPage3';
import PersonalInfoQPage4 from './components/pages/personal-info/qPage4/container';
import PersonalInfoQPage5 from './components/pages/personal-info/qPage5';
import PersonalInfoQPage5_1 from './components/pages/personal-info/qPage5_1';
import PersonalInfoQPage6 from './components/pages/personal-info/qPage6/container';
import PersonalInfoQPage7 from './components/pages/personal-info/qPage7/container';
import PersonalInfoQPage8 from './components/pages/personal-info/qPage8';
import PersonalInfoQPage9 from './components/pages/personal-info/qPage9/container';
import PersonalInfoQPage10 from './components/pages/personal-info/qPage10/container';
import HistoryQpage1 from './components/pages/history/qPage1/container';
import HistoryQpage2 from './components/pages/history/qPage2/container';
import HistoryQpage3 from './components/pages/history/qPage3/container';
import ConsentsQpage1 from './components/pages/consents/qpage1/container';
import ConsentsQpage2 from './components/pages/consents/qpage2/container';
import ConsentsQpage3 from './components/pages/consents/qpage3/container';
import ConsentsQpage4 from './components/pages/consents/qpage4/container';
import FinanceQpage1 from './components/pages/finance/qPage1/container';
import AgentDetailsPage from './components/pages/agent-details';
import PreCasInterviewSectionStart from './components/pages/interview/sectionStart/container';
import PreCasInterviewQPage1 from './components/pages/interview/qPage1/container';
import PreCasInterviewQPage2 from './components/pages/interview/qPage2/container';
import PreCasInterviewQPage3 from './components/pages/interview/qPage3/container';
import PreCasInterviewQPage4 from './components/pages/interview/qPage4/container';
import PreCasInterviewSectionComplete from './components/pages/interview/sectionComplete/container';
import VisaPrepFormAddPIPage1 from './components/pages/visa/additional-personal-info/qPage1';
import VisaPrepFormAddPIPage2 from './components/pages/visa/additional-personal-info/qPage2';
import VisaPrepFormAddHistoryPage1 from './components/pages/visa/additional-history/qPage1';
import VisaPrepFormAddHistoryPage2 from './components/pages/visa/additional-history/qPage2';
import VisaPrepFormAddHistoryPage3 from './components/pages/visa/additional-history/qPage3';
import VisaPrepFormAddHistoryPage4 from './components/pages/visa/additional-history/qPage4';
import VisaPrepFormAddHistoryPage5 from './components/pages/visa/additional-history/qPage5';
import VisaPrepFormAddHistoryPage6 from './components/pages/visa/additional-history/qPage6';
import VisaPrepFormAddHistoryPage7 from './components/pages/visa/additional-history/qPage7';
import VisaPrepFormAddHistoryPage8 from './components/pages/visa/additional-history/qPage8';
import VisaPrepFormFamilyPage1 from './components/pages/visa/family/qPage1';
import VisaPrepFormFamilyPage2 from './components/pages/visa/family/qPage2';
import VisaPrepFormFamilyPage3 from './components/pages/visa/family/qPage3';
import VisaPrepFormFamilyPage4 from './components/pages/visa/family/qPage4';
import VisaPrepFormFamilyPage5 from './components/pages/visa/family/qPage5';
import VisaPrepFormOtherTravellersPage1 from './components/pages/visa/other-travellers/qPage1';
import VisaPrepFormArrivalPage1 from './components/pages/visa/arrival/qPage1';
import VisaPrepFormDeclarationsPage1 from './components/pages/visa/declarations/qPage1';
import VisaPrepFormUniEnrolmentPage1 from './components/pages/visa/uni-enrolment/qPage1';
import VisaPrepFormUniEnrolmentPage2 from './components/pages/visa/uni-enrolment/qPage2';
import VisaPrepFormUniEnrolmentPage3 from './components/pages/visa/uni-enrolment/qPage3';
import VisaPrepFormFeedbackPage1 from './components/pages/visa/feedback/qPage1';
import CourseConflictInfo from './components/pages/courseConflict/container';
import HistorySectionStart from './components/pages/history/sectionStart/container';
import ConsentsSectionStart from './components/pages/consents/sectionStart/container';
import FinancesSectionStart from './components/pages/finance/sectionStart/container';
import ErrorTestPage from './components/pages/testing/errorPage';
import { UserRole } from './store/auth/types';
import {
  visaPrepFlags,
  canShowAgentQuestion,
  canShowVisaSupportQuestion,
  canShowWhatsappQuestion,
  canShowOfferEvidenceQuestion,
} from './utilities/pages';
import { IStudentConfig, NamedRoutes } from './types';
import { SectionStatusType } from 'api-paths-cas';
import Testing from './components/pages/testing';
import QuestionDetails from './components/pages/watchInterview/components/QuestionDetails';
import AssessInterview from './components/pages/watchInterview/components/AssessInterview';
import IdentityDetails from './components/pages/watchInterview/components/IdentityDetails';
// OffersDashboardPage moved to apply-uni (/admissions is an Apply admin route).

// ApplicantDashboardPage, ApplicantDetailsPage, WatchApplyInterviewPage moved
// to apply-uni alongside the applicant-dashboard tree.

const WatchInterviewPage = compose<React.FC>(wrapPage)(WatchInterview);

const LegalAdviseDisclaimerPage = compose<React.FC>(wrapPage, withContainer())(LegalAdviseDisclaimer);
const InfoProcessingPage = compose<React.FC>(wrapPage, withContainer())(InfoProcessing);
const PersonalInfoStart = compose<React.FC>(wrapPage, withContainer())(PersonalInfoSectionStart);
const PersonalInfoIntro = compose<React.FC>(wrapPage, withContainer())(PersonalInfoSectionIntro);
const PersonalInfoQ1P1 = compose<React.FC>(wrapPage, withContainer())(PersonalInfoQPage1);
const PersonalInfoQ1P2 = compose<React.FC>(wrapPage, withContainer())(PersonalInfoQPage2);
const PersonalInfoQ1P3 = compose<React.FC>(wrapPage, withContainer())(PersonalInfoQPage3);
const PersonalInfoQ1P4 = compose<React.FC>(wrapPage, withContainer())(PersonalInfoQPage4);

const PersonalInfoQ1P5 = compose<React.FC>(wrapPage, withContainer())(PersonalInfoQPage5);
const PersonalInfoQ1P51 = compose<React.FC>(wrapPage, withContainer())(PersonalInfoQPage5_1);
const PersonalInfoQ1P6 = compose<React.FC>(wrapPage, withContainer())(PersonalInfoQPage6);
const PersonalInfoQ1P7 = compose<React.FC>(wrapPage, withContainer())(PersonalInfoQPage7);
const PersonalInfoQ1P8 = compose<React.FC>(wrapPage, withContainer())(PersonalInfoQPage8);
const PersonalInfoQ1P9 = compose<React.FC>(wrapPage, withContainer())(PersonalInfoQPage9);
const PersonalInfoQ1P10 = compose<React.FC>(wrapPage, withContainer())(PersonalInfoQPage10);

const HistorySectionStartPage = compose<React.FC>(wrapPage, withContainer())(HistorySectionStart);
const HistoryQ1P1 = compose<React.FC>(wrapPage, withContainer())(HistoryQpage1);
const HistoryQ1P2 = compose<React.FC>(wrapPage, withContainer())(HistoryQpage2);
const HistoryQ1P3 = compose<React.FC>(wrapPage, withContainer())(HistoryQpage3);

const ConsentsSectionStartPage = compose<React.FC>(wrapPage, withContainer())(ConsentsSectionStart);
const ConsentsQ1P1 = compose<React.FC>(wrapPage, withContainer())(ConsentsQpage1);
const ConsentsQ1P2 = compose<React.FC>(wrapPage, withContainer())(ConsentsQpage2);
const ConsentsQ1P3 = compose<React.FC>(wrapPage, withContainer())(ConsentsQpage3);
const ConsentsQ1P4 = compose<React.FC>(wrapPage, withContainer())(ConsentsQpage4);

const FinancesSectionStartPage = compose<React.FC>(wrapPage, withContainer())(FinancesSectionStart);

const FinanceQ1P1 = compose<React.FC>(wrapPage, withContainer())(FinanceQpage1);
const AgentDetailsQ1P1 = compose<React.FC>(wrapPage, withContainer())(AgentDetailsPage);

const PreCasInterviewSectionStartPage = compose<React.FC>(wrapPage, withContainer())(PreCasInterviewSectionStart);
const PreCasQP1 = compose<React.FC>(wrapPage, withContainer())(PreCasInterviewQPage1);
const PreCasQP2 = compose<React.FC>(wrapPage, withContainer())(PreCasInterviewQPage2);
const PreCasQP3 = compose<React.FC>(wrapPage, withContainer())(PreCasInterviewQPage3);
const PreCasQP4 = compose<React.FC>(wrapPage, withContainer())(PreCasInterviewQPage4);
const PreCasInterviewSectionCompletePage = compose<React.FC>(wrapPage, withContainer())(PreCasInterviewSectionComplete);

const VPFAddPIPage1 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormAddPIPage1);
const VPFAddPIPage12 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormAddPIPage2);
const VPFAddHistoryPage1 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormAddHistoryPage1);
const VPFAddHistoryPage2 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormAddHistoryPage2);
const VPFAddHistoryPage3 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormAddHistoryPage3);
const VPFAddHistoryPage4 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormAddHistoryPage4);
const VPFAddHistoryPage5 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormAddHistoryPage5);
const VPFAddHistoryPage6 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormAddHistoryPage6);
const VPFAddHistoryPage7 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormAddHistoryPage7);
const VPFAddHistoryPage8 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormAddHistoryPage8);
const VPFFamilyPage1 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormFamilyPage1);
const VPFFamilyPage2 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormFamilyPage2);
const VPFFamilyPage3 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormFamilyPage3);
const VPFFamilyPage4 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormFamilyPage4);
const VPFFamilyPage5 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormFamilyPage5);
const VPFOtherTravellersPage1 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormOtherTravellersPage1);
const VPFArrivalPage1 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormArrivalPage1);
const VPFDeclarationsPage1 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormDeclarationsPage1);
const VPFUniEnrolmentPage1 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormUniEnrolmentPage1);
const VPFUniEnrolmentPage2 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormUniEnrolmentPage2);
const VPFUniEnrolmentPage3 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormUniEnrolmentPage3);
const VPFFeedbackPage1 = compose<React.FC>(wrapPage, withContainer())(VisaPrepFormFeedbackPage1);

const CourseConflictPage = compose<React.FC>(wrapPage, withContainer())(CourseConflictInfo);

const PERSONAL_INFO = 'personal-info';
const AGENT_DETAILS = 'agent-details';
const HISTORY = 'history';
const FINANCES = 'finances';
const CONSENTS = 'consents';
const PRE_CAS_INTERVIEW = 'pre-cas-interview';
export const VISA_PREP_FORM = 'visa-prep-form';

const CustomRoutes = (): React.JSX.Element => {
  const SentryRoutes = withSentryReactRouterV6Routing(Routes);
  const studentConfig: IStudentConfig = useSelector((state: ApplicationState) => state.student.config);
  const isStudentLoaded: boolean = useSelector((state: ApplicationState) => state.data.isStudentLoaded);
  const userRole: UserRole = useSelector((state: ApplicationState) => state.auth.role);
  const serviceProviderType = useSelector((state: ApplicationState) => state.auth.serviceProviderType);
  const historySectionStatus = useSelector((state: ApplicationState) => state.student.config.historySectionStatus);
  const financeSectionStatus = useSelector((state: ApplicationState) => state.student.config.financeSectionStatus);
  const isHistorySectionActive = historySectionStatus === SectionStatusType.ACTIVE;
  const isFinanceSectionActive = financeSectionStatus === SectionStatusType.ACTIVE;
  const personalInfo = useSelector((state: ApplicationState) => state.student.personalInfo);
  const offer = useSelector((state: ApplicationState) => state.student.offer);
  const history = useSelector((state: ApplicationState) => state.student.history);
  const importData = useSelector((state: ApplicationState) => state.student.importData);
  const conflictHistory = useSelector((state: ApplicationState) => state.student.conflictHistory);
  const core = useSelector((state: ApplicationState) => state.student.core);

  const intakeAfterCutoff = useSelector(selectIntakeAfterCutoff);

  const isAgent = userRole === UserRole.Agent;
  const canEditChecklist = hasPerm(userRole, P.CHECKLIST_EDIT);
  const canEditInterview = hasPerm(userRole, P.PRE_CAS_INTERVIEW_EDIT);
  const canEditVisa = hasPerm(userRole, P.VISA_PREP_EDIT);
  const canWatchRecordedInterview = hasPerm(userRole, P.AI_INTERVIEW_ASSESSMENT_SCORES_VIEW, serviceProviderType);
  const agentDetailsEnabled = useSelector((state: ApplicationState) => state.student.config.agentDetailsEnabled);

  const {
    nonHomeCountryApplication,
    hasTravelledToUk,
    hasLeaveToRemain,
    hasBorderRefusal,
    hasOtherTravellers,
    hasTakenEnglishTest,
  } = visaPrepFlags(personalInfo, offer, history);

  const showAgentQuestion = canShowAgentQuestion(importData, userRole, offer);

  const showVisaSupportQuestion = canShowVisaSupportQuestion(personalInfo, importData, studentConfig, userRole, offer);
  const showWhatsappQuestion = canShowWhatsappQuestion(offer, userRole);
  const showOfferEvidenceQuestion = canShowOfferEvidenceQuestion(
    offer,
    importData,
    conflictHistory,
    studentConfig.evidenceForOfferInCourseDetails,
  );

  const getRedirect = (): React.JSX.Element => {
    if (isAgent && !core.infoProcessingConsent && !offer?.isPISectionStarted) {
      return (
        <Navigate
          to={`/student/${core.university}/${core.student_id.split('#')[1]}/${offer.intakeId}/${offer.offerId}/landing`}
        />
      );
    }
    return <PersonalInfoQ1P1 />;
  };

  const { isCourseConflict } = offer;

  /* "Course Conflict" is a special state when student course data isn't valid
   * and student is blocked from the journey until this conflict is sorted out
   */
  if (isCourseConflict && studentConfig.enableStudentCourseBlock === true) {
    return (
      <SentryRoutes>
        <Route path={'/'} element={<CourseConflictPage />} />
        <Route path="*" element={<Navigate to="/" />} />
      </SentryRoutes>
    );
  }
  return (
    <SentryRoutes>
      {canEditChecklist && isAgent && (
        <Route path={'/student/:uni/:id/:intakeId/:offerId/landing'} element={<LegalAdviseDisclaimerPage />} />
      )}
      {canEditChecklist && isAgent && (
        <Route path={'/student/:uni/:id/:intakeId/:offerId/infoProcessing'} element={<InfoProcessingPage />} />
      )}
      {canEditChecklist && isAgent && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${PERSONAL_INFO}/section-start`}
          element={<PersonalInfoStart />}
        />
      )}
      {canEditChecklist && isAgent && Object.keys(importData).length > 0 && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${PERSONAL_INFO}/introduction`}
          element={<PersonalInfoIntro />}
        />
      )}
      {canEditChecklist && !studentConfig.Checklist?.PersonalInfo?.Page1?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${PERSONAL_INFO}/step-1`} element={getRedirect()} />
      )}
      {canEditChecklist && !studentConfig.Checklist?.PersonalInfo?.Page2?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${PERSONAL_INFO}/step-2`} element={<PersonalInfoQ1P2 />} />
      )}
      {canEditChecklist && !studentConfig.Checklist?.PersonalInfo?.Page3?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${PERSONAL_INFO}/step-3`} element={<PersonalInfoQ1P3 />} />
      )}
      {canEditChecklist && !studentConfig.Checklist?.PersonalInfo?.Page4?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${PERSONAL_INFO}/step-4`} element={<PersonalInfoQ1P4 />} />
      )}
      {canEditChecklist && !studentConfig.Checklist?.PersonalInfo?.Page5?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${PERSONAL_INFO}/step-5`} element={<PersonalInfoQ1P5 />} />
      )}
      {canEditChecklist &&
        (!isStudentLoaded ||
          (showOfferEvidenceQuestion && !studentConfig.Checklist?.PersonalInfo?.Page5_1?.hidden)) && (
          <Route
            path={`/student/:uni/:id/:intakeId/:offerId/${PERSONAL_INFO}/step-5-1`}
            element={<PersonalInfoQ1P51 />}
          />
        )}
      {canEditChecklist && !studentConfig.Checklist?.PersonalInfo?.Page6?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${PERSONAL_INFO}/step-6`} element={<PersonalInfoQ1P6 />} />
      )}
      {canEditChecklist && !studentConfig.Checklist?.PersonalInfo?.Page7?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${PERSONAL_INFO}/step-7`} element={<PersonalInfoQ1P7 />} />
      )}
      {canEditChecklist &&
        (!isStudentLoaded || showAgentQuestion) &&
        !studentConfig.Checklist?.PersonalInfo?.Page8?.hidden && (
          <Route path={`/student/:uni/:id/:intakeId/:offerId/${PERSONAL_INFO}/step-8`} element={<PersonalInfoQ1P8 />} />
        )}
      {canEditChecklist &&
        (!isStudentLoaded || showVisaSupportQuestion) &&
        !studentConfig.Checklist?.PersonalInfo?.Page9?.hidden && (
          <Route path={`/student/:uni/:id/:intakeId/:offerId/${PERSONAL_INFO}/step-9`} element={<PersonalInfoQ1P9 />} />
        )}
      {canEditChecklist &&
        (!isStudentLoaded || showWhatsappQuestion) &&
        !studentConfig.Checklist?.PersonalInfo?.Page10?.hidden && (
          <Route
            path={`/student/:uni/:id/:intakeId/:offerId/${PERSONAL_INFO}/step-10`}
            element={<PersonalInfoQ1P10 />}
          />
        )}
      {isHistorySectionActive &&
        canEditChecklist &&
        isAgent &&
        (!studentConfig.Checklist?.History?.Page1?.hidden ||
          !studentConfig.Checklist?.History?.Page2?.hidden ||
          !studentConfig.Checklist?.History?.Page3?.hidden) && (
          <Route
            path={`/student/:uni/:id/:intakeId/:offerId/${HISTORY}/section-start`}
            element={<HistorySectionStartPage />}
          />
        )}
      {isHistorySectionActive && canEditChecklist && !studentConfig.Checklist?.History?.Page1?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${HISTORY}/step-1`} element={<HistoryQ1P1 />} />
      )}
      {isHistorySectionActive && canEditChecklist && !studentConfig.Checklist?.History?.Page2?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${HISTORY}/step-2`} element={<HistoryQ1P2 />} />
      )}
      {isHistorySectionActive && canEditChecklist && !studentConfig.Checklist?.History?.Page3?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${HISTORY}/step-3`} element={<HistoryQ1P3 />} />
      )}
      {canEditChecklist &&
        isAgent &&
        (!studentConfig.Checklist?.Consents?.Page1?.hidden ||
          !studentConfig.Checklist?.Consents?.Page2?.hidden ||
          !studentConfig.Checklist?.Consents?.Page3?.hidden ||
          !studentConfig.Checklist?.Consents?.Page4?.hidden) && (
          <Route
            path={`/student/:uni/:id/:intakeId/:offerId/${CONSENTS}/section-start`}
            element={<ConsentsSectionStartPage />}
          />
        )}
      {canEditChecklist && !studentConfig.Checklist?.Consents?.Page1?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${CONSENTS}/step-1`} element={<ConsentsQ1P1 />} />
      )}
      {canEditChecklist && !studentConfig.Checklist?.Consents?.Page2?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${CONSENTS}/step-2`} element={<ConsentsQ1P2 />} />
      )}
      {canEditChecklist && !studentConfig.Checklist?.Consents?.Page3?.hidden && offer.visaSupport && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${CONSENTS}/step-3`} element={<ConsentsQ1P3 />} />
      )}
      {canEditChecklist && !studentConfig.Checklist?.Consents?.Page4?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${CONSENTS}/step-4`} element={<ConsentsQ1P4 />} />
      )}
      {isFinanceSectionActive && canEditChecklist && isAgent && !studentConfig.Checklist?.Finances?.Page1?.hidden && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${FINANCES}/section-start`}
          element={<FinancesSectionStartPage />}
        />
      )}
      {isFinanceSectionActive && canEditChecklist && !studentConfig.Checklist?.Finances?.Page1?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${FINANCES}/step-1`} element={<FinanceQ1P1 />} />
      )}
      {canEditChecklist && offer.withAgent && agentDetailsEnabled && intakeAfterCutoff && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${AGENT_DETAILS}/step-1`} element={<AgentDetailsQ1P1 />} />
      )}
      {canEditInterview && isAgent && !studentConfig.Interview?.Page1?.hidden && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${PRE_CAS_INTERVIEW}/section-start`}
          element={<PreCasInterviewSectionStartPage />}
        />
      )}
      {canEditInterview && !studentConfig.Interview?.Page1?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${PRE_CAS_INTERVIEW}/step-1`} element={<PreCasQP1 />} />
      )}
      {canEditInterview && !studentConfig.Interview?.Page2?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${PRE_CAS_INTERVIEW}/step-2`} element={<PreCasQP2 />} />
      )}
      {canEditInterview && !studentConfig.Interview?.Page3?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${PRE_CAS_INTERVIEW}/step-3`} element={<PreCasQP3 />} />
      )}
      {canEditInterview && !studentConfig.Interview?.Page4?.hidden && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/${PRE_CAS_INTERVIEW}/step-4`} element={<PreCasQP4 />} />
      )}
      {canEditInterview && isAgent && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${PRE_CAS_INTERVIEW}/section-complete`}
          element={<PreCasInterviewSectionCompletePage />}
        />
      )}
      {canEditVisa && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/additional-personal-info/step-1`}
          element={<VPFAddPIPage1 />}
        />
      )}
      {canEditVisa && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/additional-personal-info/step-2`}
          element={<VPFAddPIPage12 />}
        />
      )}
      {canEditVisa && (!isStudentLoaded || nonHomeCountryApplication === true) && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/additional-history/step-1`}
          element={<VPFAddHistoryPage1 />}
        />
      )}
      {canEditVisa && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/additional-history/step-2`}
          element={<VPFAddHistoryPage2 />}
        />
      )}
      {canEditVisa && (!isStudentLoaded || hasTravelledToUk === true) && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/additional-history/step-3`}
          element={<VPFAddHistoryPage3 />}
        />
      )}
      {canEditVisa && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/additional-history/step-4`}
          element={<VPFAddHistoryPage4 />}
        />
      )}
      {canEditVisa && (!isStudentLoaded || hasLeaveToRemain === true) && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/additional-history/step-5`}
          element={<VPFAddHistoryPage5 />}
        />
      )}
      {canEditVisa && (!isStudentLoaded || hasBorderRefusal === true) && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/additional-history/step-6`}
          element={<VPFAddHistoryPage6 />}
        />
      )}
      {canEditVisa && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/additional-history/step-7`}
          element={<VPFAddHistoryPage7 />}
        />
      )}
      {canEditVisa && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/additional-history/step-8`}
          element={<VPFAddHistoryPage8 />}
        />
      )}
      {canEditVisa && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/family/step-1`}
          element={<VPFFamilyPage1 />}
        />
      )}
      {canEditVisa && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/family/step-2`}
          element={<VPFFamilyPage2 />}
        />
      )}
      {canEditVisa && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/family/step-3`}
          element={<VPFFamilyPage3 />}
        />
      )}
      {canEditVisa && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/family/step-4`}
          element={<VPFFamilyPage4 />}
        />
      )}
      {canEditVisa && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/family/step-5`}
          element={<VPFFamilyPage5 />}
        />
      )}
      {canEditVisa && (!isStudentLoaded || hasOtherTravellers === true) && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/other-travellers/step-1`}
          element={<VPFOtherTravellersPage1 />}
        />
      )}
      {canEditVisa && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/arrival/step-1`}
          element={<VPFArrivalPage1 />}
        />
      )}
      {canEditVisa && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/declarations/step-1`}
          element={<VPFDeclarationsPage1 />}
        />
      )}
      {canEditVisa && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/uni-enrolment/step-1`}
          element={<VPFUniEnrolmentPage1 />}
        />
      )}
      {canEditVisa && (!isStudentLoaded || hasTakenEnglishTest === false) && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/uni-enrolment/step-2`}
          element={<VPFUniEnrolmentPage2 />}
        />
      )}
      {canEditVisa && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/uni-enrolment/step-3`}
          element={<VPFUniEnrolmentPage3 />}
        />
      )}
      {canEditVisa && (
        <Route
          path={`/student/:uni/:id/:intakeId/:offerId/${VISA_PREP_FORM}/feedback/step-1`}
          element={<VPFFeedbackPage1 />}
        />
      )}

      {canWatchRecordedInterview && (
        <Route path={`/student/:uni/:id/:intakeId/:offerId/interviews/:interviewHash`} element={<WatchInterviewPage />}>
          <Route
            path="/student/:uni/:id/:intakeId/:offerId/interviews/:interviewHash/assess"
            element={<AssessInterview />}
          />
          <Route path="/student/:uni/:id/:intakeId/:offerId/interviews/:interviewHash/" element={<Questions />} />
          <Route
            path="/student/:uni/:id/:intakeId/:offerId/interviews/:interviewHash/questions/:questionNo"
            element={<QuestionDetails />}
          />
          <Route
            path="/student/:uni/:id/:intakeId/:offerId/interviews/:interviewHash/identity-details"
            element={<IdentityDetails />}
          />
        </Route>
      )}

      <Route path={`/student/:uni/:id/:intakeId/:offerId`} element={<Dashboard />} />

      <Route path={`/super-secret-error-test-page`} element={<ErrorTestPage />} />
      <Route path="/testing" element={<Testing />} />
      <Route path={'/applications'} element={<UniDashboardPage />} />
      {/* /application/* and /admissions routes moved to apply-uni. */}
      <Route path={NamedRoutes.ROOT} element={<UniDashboardPage />} />
      <Route path="*" element={<Navigate to="/" />} />
    </SentryRoutes>
  );
};

export default CustomRoutes;

export interface IRouterComponent extends React.ReactElement {
  props: {
    path: string;
  };
}

export const defaultRoutes = (): string[] => {
  return CustomRoutes()
    .props.children.filter(
      (component: IRouterComponent) =>
        component?.props?.path !== undefined && !component?.props?.path.includes('config'),
    )
    .map((component: IRouterComponent) => component.props.path);
};
